// src/features/system/devices/components/iot/IoTFirmwarePanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Device details & metadata table — shows scope-based identity fields,
// authorization status, platform fingerprint, and approval info for every
// registered device.  (Formerly the "Firmware" panel — firmware-specific
// fields moved to config / telemetry.)
// ────────────────────────────────────────────────────────────────────────────
import React from 'react';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import {
  type Device,
} from '../../api/iotDevicesApi';
import { useDevicesList } from '../../hooks/useDevicesList';

function formatTs(v: string | null): string {
  if (!v) return '—';
  try {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
  } catch { return v; }
}

function timeAgo(v: string | null): string {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 0) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  } catch { return v; }
}

export default function IoTFirmwarePanel() {
  const { data: devices = [], isLoading, error } = useDevicesList();

  const columns: ColumnDef<Device>[] = [
    { header: 'Device ID',   accessor: 'device_id' },
    { header: 'Label',       accessor: 'label',          Cell: (v: string | null) => v ?? '—' },
    {
      header: 'Type',
      accessor: 'device_type',
      Cell: (v: string | null) => (
        <span className="font-mono text-xs text-sky-400">{v ?? '—'}</span>
      ),
    },
    { header: 'Platform',      accessor: 'platform',       Cell: (v: string | null) => v ?? '—' },
    { header: 'Origin',        accessor: 'origin',         Cell: (v: string | null) => v ?? '—' },
    {
      header: 'Fingerprint',
      accessor: 'fingerprint',
      Cell: (v: string | null) => (
        <span className="font-mono text-[11px] text-neutral-500">{v ? v.slice(0, 16) + '…' : '—'}</span>
      ),
    },
    { header: 'Auth Via',      accessor: 'authorized_via', Cell: (v: string | null) => v ?? '—' },
    { header: 'Approved By',   accessor: 'approved_by',    Cell: (v: string | null) => v ?? '—' },
    {
      header: 'Approved At',
      accessor: 'approved_at',
      Cell: (v: string | null) => <span className="text-xs cursor-default" title={formatTs(v)}>{timeAgo(v)}</span>,
    },
    {
      header: 'Active',
      accessor: 'is_active',
      Cell: (v: boolean) => (
        <span className={v ? 'text-emerald-400' : 'text-red-400'}>{v ? 'Yes' : 'No'}</span>
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col gap-3 overflow-hidden">
      <p className="text-xs text-neutral-500 shrink-0">
        Device identity &amp; authorization metadata. Firmware-specific fields are now part of the device configuration.
      </p>

      <div className="flex-1 min-h-0 overflow-auto">
        {error ? (
          <div className="text-red-400 text-sm p-2">{error.message}</div>
        ) : (
          <MinimalisticTable<Device>
            columns={columns}
            data={devices}
            noDataMessage={isLoading ? 'Loading…' : 'No devices registered.'}
            showNewButton={false}
          />
        )}
      </div>
    </div>
  );
}
